import apiUtils from '@elab/core/utils/apiUtils';
import CONSTANTS, { errorStatusToReason } from '@elab/core/utils/constants';

import HTTPClient from '../../utils/HTTPClient';

const getApiName = userId => `/users/${userId || 'anonymous'}/carts`;
const getCartApiName = (userId, cartId, isEgiftCardCartActive) =>
    `/${userId && !isEgiftCardCartActive ? 'current' : cartId}`;

const getApiService = userId => `${apiUtils.getBaseUri()}${getApiName(userId)}`;
const getCurrentCartApiService = (userId, cartId, isEgiftCardCartActive) =>
    `${getApiService(userId)}${getCartApiName(userId, cartId, isEgiftCardCartActive)}`;

const getCartTransactionApi = (cartId, userId, cardNumber, type = CONSTANTS.giftcardApiUrl) => {
    cardNumber = encodeURIComponent(cardNumber);
    return `${getCurrentCartApiService(userId, cartId)}${type}/${cardNumber}`;
};

const createCartParams = data => {
    data.fields = 'FULL';
    const searchParams = new URLSearchParams();

    Object.keys(data).forEach(key => data[key] && searchParams.append(key, data[key]));

    return searchParams.toString();
};

const loadCart = async (
    userId,
    cartId,
    fields = 'FULL',
    stocks = 'true',
    isEgiftCardCartActive,
    deliveryCountry,
) => {
    // TECH DEBT: BE will be in charge to check which BU has Real Time Stock activated
    const uri = `${getCurrentCartApiService(
        userId,
        cartId,
        isEgiftCardCartActive,
    )}?fields=${fields}&stocks=${stocks}&country=${deliveryCountry}`;
    const response = await HTTPClient().getNoCache(uri);
    if (!response.ok) {
        throw new Error(`Unable to load cart (${response.status})`, {
            cause: await response.json(),
        });
    }
    const json = await response.json();
    return json;
};

const getCarts = async (userId, pageSize = 1) => {
    const params = new URLSearchParams();
    if (pageSize) {
        params.append('pageSize', pageSize);
    }

    const uri = `${getApiService(userId)}?${params.toString()}`;
    const response = await HTTPClient().get(uri);
    if (!response.ok) {
        throw new Error(`Unable to load cart (${response.status})`);
    }
    return await response.json();
};

const getMostRecentCart = async userId => {
    const uri = `${getApiService(userId)}/mostRecentCart`;
    const response = await HTTPClient().get(uri);
    if (!response.ok) {
        throw new Error(`Unable to load cart (${response.status})`);
    }
    return await response.json();
};

const createCart = async (userId, oldCartId) => {
    const uri = `${getApiService(userId)}${oldCartId ? `oldCartId=${oldCartId}` : ''}`;
    const response = await HTTPClient().postJson(uri);
    if (!response.ok) {
        throw new Error(`Unable to create cart (${response.status})`);
    }
    return await response.json();
};

const deleteCart = async (userId, cartGUID) => {
    const uri = `${getApiService(userId)}/${cartGUID}`;
    const response = await HTTPClient().deleteJson(uri);
    try {
        if (!response.ok) {
            throw new Error(`Unable to remove cart (${response.status})`);
        }
        return true;
    } catch (error) {
        return false;
    }
};

const mergeCarts = async (oldCartId, toMergeCartGuid, userId) => {
    const uri = `${getApiService(userId)}?${createCartParams({
        oldCartId,
        toMergeCartGuid,
    })}`;

    const response = await HTTPClient().postJson(uri);
    if (!response.ok) {
        throw new Error(`Unable to create cart (${response.status})`);
    }
    return await response.json();
};

const addToCart = async (userId, cartId, product) => {
    // productDTO will be body of JSON and could contain deliveryPointOfService in case of RTS
    const productDTO = {
        product: {
            code: product.code,
        },
        multiGwp: product.multiGwp || false,
        quantity: product.quantity,
        ...(product.storeId
            ? {
                  deliveryPointOfService: {
                      name: product.storeId,
                  },
              }
            : {}),
    };
    const uri = `${getCurrentCartApiService(userId, cartId)}/entries`;
    const response = await HTTPClient().postJson(uri, productDTO);
    if (!response.ok && (response.status === 403 || response.status === 401)) {
        throw {
            status: response.status,
            message: `Unable to add order entry (${response.status})`,
            reason: errorStatusToReason[response.status],
        };
    }
    return await response.json();
};

const bulkAddToCart = async (userId, cartId, products, stockCheck = false) => {
    const uri = `${getCurrentCartApiService(
        userId,
        cartId,
    )}/entries/multiple?strictStockCheck=${stockCheck}`;
    const response = await HTTPClient().postJson(uri, products);
    if (!response.ok && (response.status === 403 || response.status === 401)) {
        throw {
            status: response.status,
            message: `Unable to add order entry (${response.status})`,
            reason: errorStatusToReason[response.status],
        };
    }
    return await response.json();
};

const updateCart = async (userId, cartId, entryNumber, quantity) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}/entries/${entryNumber}`;
    const response = await HTTPClient().patchJson(uri, { quantity });
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
            reason: errorStatusToReason[response.status],
        };
    }
    return await response.json();
};

const removeFromCart = async (userId, cartId, entryNumber) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}/entries/${entryNumber}`;
    const response = await HTTPClient().delete(uri);
    if (!response.ok) {
        throw new Error(`Unable to remove order entry (${response.status})`);
    }
};

const restoreOldCart = async (userId, oldCartGuid) => {
    const uri = `${getApiService(userId)}/?toMergeCartGuid=${oldCartGuid}`;
    const response = await HTTPClient().post(uri);
    return await response.json();
};

const removeEntriesByProductCode = async (userId, cartId, codes) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}/entries?`;
    const params = codes.map(code => `productCodes=${code}`).join('&');
    const response = await HTTPClient().delete(uri + params);
    if (!response.ok) {
        throw new Error(`Unable to remove order entry (${response.status})`);
    }
};

const removeAllCartEntries = async (userId, cartId) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}`;

    const response = await HTTPClient().delete(uri);
    if (!response.ok) {
        throw new Error(`Unable to remove cart entries (${response.status})`);
    }
};

const adjustCart = async (userId, cartId, payload = {}) => {
    const body = {};
    if (payload.hasForbiddenStock) {
        body.country = payload.deliveryCountry;
        if (payload.hasOnlyForbiddenEntryInCart) {
            body.adjustmentType = 'FORBIDDEN_COUNTRY';
        } else {
            body.adjustmentGroup = 'adjustment.group.delivery';
        }
    }
    if (payload.maxallowedsettlement) {
        body.adjustmentType = 'DELIVERY_MODE_MAX_ALLOWED_PRODUCT_QUANTITY';
    }
    const uri = `${getCurrentCartApiService(userId, cartId)}/adjust?${createCartParams(body)}`;

    const response = await HTTPClient().postJson(uri);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }

    return await response.json();
};

const limitativeBrands = async (userId, cartId) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}/limitativeBrands`;
    const response = await HTTPClient().delete(uri);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }

    return await response.json();
};

const overlimitedBrands = async (userId, cartId) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}/overlimitedBrands`;
    const response = await HTTPClient().get(uri);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }

    return await response.json();
};

const setEmailAddress = async (userId, cartId, email) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}/email?email=${email}`;

    const response = await HTTPClient().putJson(uri);
    if (!response.ok) {
        throw new Error(`Unable to set email (${response.status})`);
    }
};

const setDeliveryAddress = async (userId, cartId, addressDto) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}/addresses/delivery`;

    const response = await HTTPClient().postJson(uri, addressDto);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }
};

const putDeliveryAddress = async (
    userId,
    cartId,
    addressId,
    isEgiftCardCartActive,
    deliveryCountry,
) => {
    const uri = `${getCurrentCartApiService(
        userId,
        cartId,
    )}/addresses/delivery?addressId=${addressId}${
        deliveryCountry ? `&country=${deliveryCountry}` : ''
    }`;

    const response = await HTTPClient().putJson(uri);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }
};

const putBillingAddress = async (userId, cartId, addressId, isEgiftCardCartActive) => {
    const uri = `${getCurrentCartApiService(
        userId,
        cartId,
        isEgiftCardCartActive,
    )}/addresses/billing/${addressId}`;

    const response = await HTTPClient().putJson(uri);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }
};

const removeBillingAddress = async (userId, cartId) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}/addresses/billing`;

    const response = await HTTPClient().delete(uri);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }
};

const checkoutAsGuest = async (guestDto, cartId) => {
    const uri = `${getCurrentCartApiService(null, cartId)}/guest`;
    const response = await HTTPClient().postJson(uri, guestDto);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }
};

const removeOEproducts = async (userId, cartId) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}/entries/type/online_only`;
    const response = await HTTPClient().delete(uri);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }
};

const submitGiftCard = async (userId, cartId, formUrl, dto) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}${formUrl}`;
    const response = await HTTPClient().postJson(uri, dto);
    return await response.text().then(text => {
        return text ? JSON.parse(text) : {};
    });
};

const applyGiftcardOrVoucher = async (userId, cartId, submitUrl, cardNumber) => {
    let response = {};
    if (submitUrl === CONSTANTS.voucherApiUrl) {
        const body = {
            voucherId: cardNumber,
        };
        const uri = `${getCurrentCartApiService(userId, cartId)}${submitUrl}?${createCartParams(
            body,
        )}`;
        response = await HTTPClient().postJson(uri);
    } else {
        const body = {
            giftCardNumber: cardNumber,
        };

        const uri = `${getCurrentCartApiService(userId, cartId)}${submitUrl}`;
        response = await HTTPClient().postJson(uri, body);
    }

    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }
    if (submitUrl == CONSTANTS.giftcardApiUrl) {
        return response.json();
    }
};

const applyDynamicGiftCard = async (userId, cartId, submitUrl, requestBody) => {
    const uri = `${getCurrentCartApiService(userId, cartId)}${submitUrl}`;

    const response = await HTTPClient().postJson(uri, requestBody);

    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }

    return await response.json();
};
// TECH-DEBT: It only remove Voucher for now because giftcards have issues to be solved.
// in the future we could use this function also for giftcards
const removeGiftcardOrVoucher = async (userId, cartId, cardNumber, type) => {
    const uri = `${getCartTransactionApi(cartId, userId, cardNumber, type)}`;
    const response = await HTTPClient().delete(uri);

    if (!response.ok) {
        throw new Error(`Unable to remove gift card (${response.status})`);
    }
};

const putPhoneNumber = async (userId, cartId, phoneNumber) => {
    const uri = `${getCurrentCartApiService(
        userId,
        cartId,
    )}/phoneNumber?phoneNumber=${phoneNumber}`;

    const response = await HTTPClient().putJson(uri);
    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }
};

const getHomeDeliveryEstimatedDate = async code => {
    const uri = `${apiUtils.getBaseUri()}/deliverymodes/${code}/estimation/checkout`;

    const response = await HTTPClient().get(uri);

    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }

    return response.json();
};

const getCCEstimatedDeliveryDate = async (code, latitude = 0, longitude = 0, hideTime) => {
    const deliveryModePath = `deliverymodes/${code}/estimation/checkout`;
    const storePath = `store/?latitude=${latitude}&longitude=${longitude}`;

    const uri = `${apiUtils.getBaseUri()}/${deliveryModePath}/${storePath}${
        hideTime ? '&showTime=false' : ''
    }`;

    const response = await HTTPClient().get(uri);

    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }

    return response.json();
};

const getPostPointDeliveryEstimationDate = async (code, streetNumber = 0, postalCode = 0) => {
    const deliveryModePath = `deliverymodes/${code}/estimation/checkout`;
    const storePath = `postoffice/?streetNumber=${streetNumber}&postalCode=${postalCode}`;
    const uri = `${apiUtils.getBaseUri()}/${deliveryModePath}/${storePath}`;

    const response = await HTTPClient().get(uri);

    if (!response.ok) {
        throw {
            json: await response.json(),
            status: response.status,
            message: response.message,
        };
    }

    return response.json();
};

export default {
    getApiName,
    getCartApiName,
    getCurrentCartApiService,
    loadCart,
    createCart,
    addToCart,
    setDeliveryAddress,
    setEmailAddress,
    updateCart,
    removeFromCart,
    removeAllCartEntries,
    mergeCarts,
    getCarts,
    getMostRecentCart,
    putDeliveryAddress,
    putBillingAddress,
    removeBillingAddress,
    checkoutAsGuest,
    adjustCart,
    limitativeBrands,
    overlimitedBrands,
    removeOEproducts,
    removeEntriesByProductCode,
    submitGiftCard,
    applyGiftcardOrVoucher,
    removeGiftcardOrVoucher,
    applyDynamicGiftCard,
    putPhoneNumber,
    getHomeDeliveryEstimatedDate,
    getCCEstimatedDeliveryDate,
    getPostPointDeliveryEstimationDate,
    deleteCart,
    restoreOldCart,
    bulkAddToCart,
};
